<template>
  <div class="page-wrapper">
    <div class="paper-detail">
      <div v-if="loading" class="empty-wrap">
        <div class="empty">Loading...</div>
      </div>

      <div v-else-if="!paper" class="empty-wrap">
        <div class="empty">Paper does not exist or has been deleted</div>
        <button class="btn btn-primary" @click="goBack">Back to List</button>
      </div>

      <div v-else class="detail-inner">
        <main class="detail-main">
          <div class="detail-nav">
            <button class="back-btn" @click="goBack">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="back-icon"><path d="M15 18l-6-6 6-6"/></svg>
              Back to Paper List
            </button>
            <div class="nav-right">
              <span class="status-badge" :class="{ done: isSubmitted }">
                <span class="dot-indicator"></span>
                {{ isSubmitted ? 'Submitted' : 'In Progress' }}
              </span>
              <span v-if="!isSubmitted" class="timer">{{ elapsedTime }}</span>
            </div>
          </div>

          <div class="detail-card">
            <div class="paper-header">
              <div class="paper-header-bg"></div>
              <div class="paper-header-content">
                <h1 class="paper-title">{{ paper.title || paper.name || `Paper #${paperId}` }}</h1>
                <div class="paper-meta">
                  <span v-if="paper.paper_no" class="meta-item">No. {{ paper.paper_no }}</span>
                  <span v-if="paper.start_time" class="meta-item">Start {{ paper.start_time }}</span>
                  <span v-if="paper.submit_time" class="meta-item">Submit {{ paper.submit_time }}</span>
                </div>
                <div class="paper-stats">
                  <div class="stat-item">
                    <span class="stat-num">{{ questions.length }}</span>
                    <span class="stat-label">Questions</span>
                  </div>
                  <div class="stat-divider"></div>
                  <div class="stat-item">
                    <span class="stat-num">{{ paper.total_score || 100 }}</span>
                    <span class="stat-label">Full Score</span>
                  </div>
                  <template v-if="isSubmitted">
                    <div class="stat-divider"></div>
                    <div class="stat-item">
                      <span class="stat-num" :class="isPass ? 'pass' : 'fail'">{{ correctCount }}</span>
                      <span class="stat-label">Correct</span>
                    </div>
                    <div class="stat-divider"></div>
                    <div class="stat-item">
                      <span class="stat-num" :class="isPass ? 'pass' : 'fail'">{{ paper.user_total_score ?? scoreFromItems }}</span>
                      <span class="stat-label">Score</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="isSubmitted" class="submit-notice" :class="{ pass: isPass }">
              <div class="notice-icon">{{ isPass ? '🎉' : '💪' }}</div>
              <div class="notice-body">
                <div class="notice-title">{{ isPass ? 'Congratulations, you passed!' : 'Keep it up!' }}</div>
                <div class="notice-sub">
                  Correct {{ correctCount }} · Wrong {{ questions.length - correctCount }} · Time {{ elapsedTime }}
                </div>
              </div>
              <div v-if="paper.pass_score !== undefined" class="notice-pass">
                Passing Score {{ paper.pass_score }}
              </div>
            </div>

            <div class="questions">
              <div
                v-for="(q, idx) in questions"
                :key="q.id || idx"
                :id="`q-${idx}`"
                class="question-item"
                :class="{ answered: hasAnswer(q), correct: isSubmitted && isCorrect(q), wrong: isSubmitted && !isCorrect(q) && hasAnswer(q) }"
              >
                <div class="q-header">
                  <span class="q-index" :class="{ answered: hasAnswer(q) }">{{ idx + 1 }}</span>
                  <div class="q-center">
                    <div class="q-text">
                      {{ q.title || q.content || q.question }}
                      <span v-if="q.score !== undefined" class="q-score">{{ q.score }} pts</span>
                    </div>
                    <div v-if="isSubmitted" class="q-meta">
                      <span v-if="q.difficulty" class="q-tag" :class="difficultyClass(q.difficulty)">{{ q.difficulty }}</span>
                      <span v-if="q.category" class="q-tag cat">{{ q.category }}</span>
                      <span class="q-result" :class="{ correct: isCorrect(q) }">
                        {{ qResultText(q) }}
                      </span>
                    </div>
                  </div>
                  </div>
                  <div class="q-body">
                  <div class="options" v-if="q.options && q.options.length">
                    <div
                      v-for="opt in q.options"
                      :key="opt.id || opt.key || opt.option_key"
                      class="option-item"
                      :class="getOptionClass(q, opt)"
                      @click="!isSubmitted && selectOption(q, opt)"
                    >
                      <span class="option-key">{{ opt.id || opt.key || opt.option_key }}</span>
                      <span class="option-text">{{ opt.option_text || opt.content || opt.text || opt.option_value }}</span>
                      <span v-if="isSubmitted && isCorrectOption(q, opt)" class="option-mark">✓</span>
                      <span v-if="isSubmitted && isWrongSelected(q, opt)" class="option-mark wrong">✗</span>
                    </div>
                  </div>
                  <div v-else class="no-options">
                    <span class="no-options-ic">📝</span>
                    <span>No options available for this question</span>
                  </div>

                  <div v-if="isSubmitted && q.knowledge" class="q-knowledge">
                    <span class="kl-label">Knowledge Point</span>
                    <span class="kl-text">{{ q.knowledge }}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </main>
      </div>

      <div v-if="!loading && paper" class="bottom-bar">
        <div class="bottom-bar-inner">
          <div class="progress-info">
            <div class="progress-text">
              Completed <span class="progress-num">{{ answeredCount }}</span> / {{ questions.length }}
            </div>
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
          </div>
          <button
            v-if="!isSubmitted"
            class="btn btn-primary btn-lg"
            :disabled="submitting"
            @click="handleSubmit"
          >
            <span v-if="submitting" class="spinner"></span>
            {{ submitting ? 'Submitting...' : 'Submit Paper' }}
          </button>
          <button
            v-else
            class="btn btn-primary btn-lg"
            @click="fetchDetail"
          >
            Refresh to View Latest Results
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '@/utils/request'

const route = useRoute()
const router = useRouter()

const paperId = computed(() => route.params.id)
const paper = ref(null)
const questions = ref([])
const answers = ref({})
const loading = ref(false)
const submitting = ref(false)

let timerInterval = null
const startTime = ref(Date.now())
const tick = ref(0)

const isSubmitted = computed(() => {
  if (!paper.value) return false
  return paper.value.status === 1 || paper.value.status === 'submitted'
})

const isPass = computed(() => {
  if (!paper.value) return false
  const pass = paper.value.pass_score || paper.value.passScore || 60
  return (paper.value.user_total_score ?? scoreFromItems.value) >= pass
})

const scoreFromItems = computed(() => {
  return questions.value.reduce((s, q) => s + (q.user_score || 0), 0)
})

const correctCount = computed(() => {
  if (!isSubmitted.value) return 0
  return questions.value.filter(q => isCorrect(q)).length
})

const answeredCount = computed(() => {
  return questions.value.filter(q => hasAnswer(q)).length
})

const progressPercent = computed(() => {
  if (!questions.value.length) return 0
  return Math.round((answeredCount.value / questions.value.length) * 100)
})

const elapsedTime = computed(() => {
  if (!paper.value) return '00:00'
  if (isSubmitted.value && paper.value.submit_time && paper.value.start_time) {
    const s = new Date(paper.value.start_time).getTime()
    const e = new Date(paper.value.submit_time).getTime()
    return formatDuration(Math.max(0, e - s))
  }
  return formatDuration(Date.now() - startTime.value)
})

const formatDuration = (ms) => {
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const fetchDetail = async (forceResult = false) => {
  try {
    loading.value = true
    const url = (forceResult || isSubmitted.value) ? '/quizen/paper/result' : '/quizen/paper/detail'
    const res = await request.get(url, { params: { id: paperId.value } })
    let data = res.data || res
    let paperData = data
    let qList = []

    if (Array.isArray(data)) {
      paperData = data.find(p => String(p.id) === String(paperId.value)) || data[0]
      qList = paperData?.items || paperData?.questions || paperData?.question_list || []
    } else if (data && typeof data === 'object') {
      paperData = data.paper || data
      qList = data.items || paperData?.items || paperData?.questions || paperData?.question_list || []
    }

    paper.value = paperData
    questions.value = Array.isArray(qList) ? qList : []

    if (paperData?.start_time) {
      startTime.value = new Date(paperData.start_time).getTime()
    }

    if (paperData?.user_answers || paperData?.answers) {
      answers.value = paperData.user_answers || paperData.answers || {}
    } else {
      const map = {}
      qList.forEach(q => {
        if (q.user_answer !== undefined && q.user_answer !== null && q.user_answer !== '') {
          map[q.id] = q.user_answer
        }
      })
      if (Object.keys(map).length) answers.value = map
    }

    if (!forceResult && paperData && (paperData.status === 1 || paperData.status === 'submitted')) {
      fetchDetail(true)
    }
  } catch (e) {
    // handled by interceptor
  } finally {
    loading.value = false
  }
}

const hasAnswer = (q) => {
  const a = answers.value[q.id]
  if (a !== undefined && a !== null && a !== '') return true
  if (q.user_answer !== undefined && q.user_answer !== null && q.user_answer !== '') return true
  return false
}

const selectOption = (q, opt) => {
  const key = q.id
  const optKey = opt.id || opt.key || opt.option_key || opt.value || opt.option_key
  let current = answers.value[key]
  if (q.type === 'multiple' || q.type === 2 || q.type === 'M') {
    if (!Array.isArray(current)) current = []
    const idx = current.indexOf(optKey)
    if (idx >= 0) current.splice(idx, 1)
    else current.push(optKey)
    answers.value = { ...answers.value, [key]: current }
  } else {
    answers.value = { ...answers.value, [key]: optKey }
  }
}

const getActualAnswer = (q) => {
  const a = answers.value[q.id]
  if (a !== undefined && a !== null && a !== '') return a
  return q.user_answer
}

const qResultText = (q) => {
  const userAnswer = getActualAnswer(q)
  if (userAnswer === undefined || userAnswer === null || userAnswer === '' ||
      (Array.isArray(userAnswer) && userAnswer.length === 0)) {
    return 'Not Selected ✗'
  }
  const label = Array.isArray(userAnswer) ? userAnswer.join(', ') : userAnswer
  return isCorrect(q) ? `${label} ✓` : `${label} ✗`
}

const isCorrect = (q) => {
  if (!isSubmitted.value) return false
  if (q.is_correct !== undefined && q.is_correct !== null) return Number(q.is_correct) === 1 || q.is_correct === true
  const correct = q.answer || q.correct_answer || q.correctAnswer || q.standard_answer
  const userAnswer = getActualAnswer(q)
  if (correct === undefined || correct === null) return true
  if (Array.isArray(correct) && Array.isArray(userAnswer)) {
    return correct.length === userAnswer.length && correct.every(c => userAnswer.includes(c))
  }
  return String(correct) === String(userAnswer)
}

const isCorrectOption = (q, opt) => {
  const correct = q.answer || q.correct_answer || q.correctAnswer || q.standard_answer
  const optKey = opt.id || opt.key || opt.option_key || opt.value
  if (Array.isArray(correct)) return correct.includes(optKey)
  return String(correct) === String(optKey)
}

const isWrongSelected = (q, opt) => {
  if (isCorrectOption(q, opt)) return false
  const userAnswer = getActualAnswer(q)
  const optKey = opt.id || opt.key || opt.option_key || opt.value
  if (Array.isArray(userAnswer)) return userAnswer.includes(optKey)
  return String(userAnswer) === String(optKey)
}

const getOptionClass = (q, opt) => {
  const result = []
  const optKey = opt.id || opt.key || opt.option_key || opt.value

  if (!isSubmitted.value) {
    const a = answers.value[q.id]
    if (Array.isArray(a) && a.includes(optKey)) result.push('selected')
    else if (a !== undefined && String(a) === String(optKey)) result.push('selected')
    return result
  }

  if (isCorrectOption(q, opt)) result.push('correct')
  if (isWrongSelected(q, opt)) result.push('wrong')
  return result
}

const difficultyClass = (d) => {
  if (!d) return ''
  if (d.includes('难')) return 'hard'
  if (d.includes('易')) return 'easy'
  return 'mid'
}

const handleSubmit = async () => {
  try {
    const unanswered = questions.value.length - answeredCount.value
    if (unanswered > 0) {
      await ElMessageBox.confirm(`You still have ${unanswered} unanswered questions. Are you sure you want to submit?`, 'Notice', {
        confirmButtonText: 'Submit',
        cancelButtonText: 'Continue',
        type: 'warning',
      })
    }
    submitting.value = true
    await request.post('/quizen/paper/submit', {
      paper_id: paperId.value,
      answers: answers.value,
    })
    ElMessage.success('Submitted successfully')
    if (paper.value) {
      paper.value = { ...paper.value, status: 1, submit_time: new Date().toISOString() }
    }
    fetchDetail(true)
  } catch (e) {
    if (e !== 'cancel') {
      // handled by interceptor
    }
  } finally {
    submitting.value = false
  }
}

const goBack = () => {
  router.push('/quizen/paper')
}

onMounted(() => {
  fetchDetail()
  timerInterval = setInterval(() => {
    if (!isSubmitted.value) {
      tick.value++
    }
  }, 1000)
})

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval)
})

watch(isSubmitted, (v) => {
  if (v && timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
})
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background:
    radial-gradient(ellipse at top left, rgba(99, 102, 241, 0.08), transparent 50%),
    radial-gradient(ellipse at bottom right, rgba(236, 72, 153, 0.06), transparent 50%),
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.paper-detail {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 20px 160px;
  box-sizing: border-box;
  position: relative;
}

.detail-inner {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  position: relative;
}

.detail-main {
  flex: 1;
  min-width: 0;
}

.empty-wrap {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.empty {
  color: #6b7280;
  margin-bottom: 20px;
}

.detail-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #6366f1;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
}

.back-btn:hover {
  background: #f9fafb;
  border-color: #c7d2fe;
  transform: translateX(-2px);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.12);
}

.back-icon {
  width: 18px;
  height: 18px;
}

.timer {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  color: #6366f1;
  background: #eef2ff;
  padding: 4px 10px;
  border-radius: 99px;
  font-weight: 600;
}

.status-badge {
  font-size: 12px;
  padding: 5px 14px;
  border-radius: 99px;
  background: #fef3c7;
  color: #92400e;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.status-badge.done {
  background: #d1fae5;
  color: #065f46;
}

.dot-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.detail-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 28px 32px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
}

.paper-header {
  position: relative;
  padding: 32px 36px;
  margin: -28px -32px 28px;
  overflow: hidden;
  border-radius: 14px 14px 0 0;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);
  box-shadow: 0 10px 40px -10px rgba(99, 102, 241, 0.4);
}

.paper-header-bg {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 35%),
    radial-gradient(circle at 60% 60%, rgba(255, 255, 255, 0.08) 0%, transparent 30%);
  pointer-events: none;
}

.paper-header-content {
  position: relative;
  text-align: center;
  padding: 0;
  color: #fff;
}

.paper-title {
  margin: 0 0 8px 0;
  font-size: 26px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
}

.paper-meta {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.meta-item {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 3px 10px;
  border-radius: 99px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.paper-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}

.stat-num.pass { color: #86efac; }
.stat-num.fail { color: #fca5a5; }

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
}

.stat-divider {
  width: 1px;
  height: 22px;
  background: rgba(255, 255, 255, 0.3);
}

.submit-notice {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.submit-notice.pass {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #bbf7d0;
}

.notice-icon {
  font-size: 32px;
}

.notice-body { flex: 1; }

.notice-title {
  font-size: 16px;
  font-weight: 600;
  color: #0c4a6e;
}

.submit-notice.pass .notice-title { color: #065f46; }

.notice-sub {
  font-size: 13px;
  color: #475569;
  margin-top: 2px;
}

.notice-pass {
  font-size: 12px;
  color: #92400e;
  background: #fef3c7;
  padding: 4px 10px;
  border-radius: 6px;
  flex-shrink: 0;
}

.questions {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.question-item {
  padding: 22px 24px;
  border-bottom: none;
  transition: background 0.2s;
  background: #fafbfc;
  border-radius: 12px;
  border: 1px solid #eef0f4;
}

.question-item.correct {
  background: rgba(16, 185, 129, 0.06);
  border-color: #bbf7d0;
}

.question-item.wrong {
  background: rgba(239, 68, 68, 0.05);
  border-color: #fecaca;
}

.q-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 16px;
}

.q-index {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #eef2ff, #ede9fe);
  color: #6366f1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.q-index.answered {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
}

.question-item.correct .q-index {
  background: #10b981;
}

.question-item.wrong .q-index {
  background: #ef4444;
}

.q-center {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.q-meta {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}

.q-tag {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 99px;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 500;
}

.q-tag.cat {
  background: #f0f9ff;
  color: #0369a1;
}

.q-tag.easy { background: #dcfce7; color: #166534; }
.q-tag.mid { background: #fef3c7; color: #92400e; }
.q-tag.hard { background: #fee2e2; color: #991b1b; }

.q-score {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
  margin-left: 8px;
}

.q-result {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 12px;
  border-radius: 99px;
  flex-shrink: 0;
  background: #fee2e2;
  color: #991b1b;
}

.q-result.correct {
  background: #d1fae5;
  color: #065f46;
}

.q-text {
  font-size: 17px;
  color: #0f172a;
  line-height: 1.75;
  white-space: pre-wrap;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
  color: #374151;
  background: #fff;
}

.option-item:hover:not(.correct):not(.wrong) {
  border-color: #60a5fa;
  background: #f0f9ff;
  transform: translateX(2px);
}

.option-item.selected {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1e40af;
}

.option-item.correct {
  border-color: #10b981;
  background: #ecfdf5;
  color: #065f46;
  cursor: default;
}

.option-item.wrong {
  border-color: #ef4444;
  background: #fef2f2;
  color: #991b1b;
  cursor: default;
}

.option-key {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.option-item.selected .option-key {
  background: #3b82f6;
  color: #fff;
}

.option-item.correct .option-key {
  background: #10b981;
  color: #fff;
}

.option-item.wrong .option-key {
  background: #ef4444;
  color: #fff;
}

.option-text {
  flex: 1;
  line-height: 1.6;
}

.option-mark {
  color: #10b981;
  font-weight: 700;
  flex-shrink: 0;
  font-size: 16px;
}

.option-mark.wrong {
  color: #ef4444;
}

.no-options {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  color: #9ca3af;
}

.no-options-ic { font-size: 16px; }

.q-knowledge {
  margin-top: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border-left: 3px solid #6366f1;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.kl-label {
  color: #6366f1;
  font-weight: 600;
  flex-shrink: 0;
}

.kl-text {
  color: #475569;
  line-height: 1.6;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(24px) saturate(1.8);
  -webkit-backdrop-filter: blur(24px) saturate(1.8);
  border-top: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 -8px 32px rgba(15, 23, 42, 0.08);
}

.bottom-bar-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
}

.progress-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.progress-text {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
}

.progress-num {
  font-size: 16px;
  font-weight: 700;
  color: #6366f1;
}

.progress-track {
  height: 6px;
  background: rgba(226, 232, 240, 0.6);
  border-radius: 99px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
  border-radius: 99px;
  transition: width 0.3s ease;
}

.submit-bar {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
}

.btn-primary {
  background: #fff;
  color: #6366f1;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-lg {
  padding: 12px 40px;
  font-size: 15px;
}

.btn.full { width: 100%; justify-content: center; }

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(99, 102, 241, 0.25);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .paper-detail {
    padding: 14px 12px 180px;
  }

  .detail-card {
    padding: 18px 16px;
  }

  .paper-header {
    padding: 24px 20px;
    margin: -18px -16px 24px;
    border-radius: 14px 14px 0 0;
  }

  .paper-title { font-size: 20px; }

  .paper-stats { gap: 12px; }
  .stat-num { font-size: 18px; }
  .stat-label { font-size: 12px; }

  .q-text { font-size: 15px; }

  .detail-nav { gap: 8px; }

  .option-item { padding: 11px 12px; font-size: 13px; }

  .q-knowledge { flex-direction: column; }

  .question-item { padding: 18px 16px; }

  .bottom-bar-inner {
    padding: 12px 16px;
    gap: 14px;
  }

  .progress-text { font-size: 12px; }
  .progress-num { font-size: 15px; }
}

@media (max-width: 600px) {
  .paper-detail {
    padding: 14px 12px 220px;
  }

  .bottom-bar-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px 14px 14px;
  }

  .bottom-bar-inner > .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .paper-stats { flex-wrap: wrap; }
  .stat-divider { display: none; }
}
</style>